import type { User } from '../api/users';
import { getGlobalLegal } from '../api/legal';
import type { GlobalLegalDocument } from '../models/legal';
import type { StoredAuthUser } from '../config/infra';

export type GlobalLegalState = GlobalLegalDocument;

export function getRequestedOrganizationIdFromSearch(
  search: string
): string | null {
  const params = new URLSearchParams(search);
  const requested =
    params.get('organizationId') ||
    params.get('organization_id') ||
    params.get('org');
  const normalized = requested?.trim();
  return normalized ? normalized : null;
}

/** Platform god detection — matches server auth and storage rules. */
export function claimsIndicateGodRole(
  claims: Record<string, unknown> | undefined
): boolean {
  if (!claims) return false;
  if (claims.role === 'god') return true;
  const platformRoles = claims.platformRoles;
  if (Array.isArray(platformRoles) && platformRoles.includes('god')) {
    return true;
  }
  return false;
}

export function isAdminUserRole(role?: string | null): boolean {
  return role === 'admin';
}

export function resolveIsGodMode(
  isGodFromClaims: boolean,
  userRole?: string | null
): boolean {
  return isGodFromClaims || userRole === 'god';
}

export function canAccessAdminApp(options: {
  isGodMode: boolean;
  isAdminUser: boolean;
}): boolean {
  return options.isGodMode || options.isAdminUser;
}

export function resolveFirebaseAdminAccess(
  isGodFromClaims: boolean,
  userData: User | null
): { isGodMode: boolean; isAdminUser: boolean; canAccess: boolean } {
  const isAdminUser = isAdminUserRole(userData?.role);
  const isGodMode = resolveIsGodMode(isGodFromClaims, userData?.role);
  return {
    isGodMode,
    isAdminUser,
    canAccess: canAccessAdminApp({ isGodMode, isAdminUser }),
  };
}

export function resolveSelfHostedAdminAccess(
  userData: User | null,
  storedUser?: StoredAuthUser | null
): { isGodMode: boolean; isAdminUser: boolean; canAccess: boolean } {
  const isGodMode = claimsIndicateGodRole({
    role: storedUser?.role ?? userData?.role ?? undefined,
  });
  const isAdminUser =
    isAdminUserRole(userData?.role) || isAdminUserRole(storedUser?.role);
  return {
    isGodMode,
    isAdminUser,
    canAccess: canAccessAdminApp({ isGodMode, isAdminUser }),
  };
}

/** Org ids for bootstrap — server profile first, then session fallbacks. */
export function resolveOrganizationIdsForBootstrap(options: {
  userOrganizationIds?: string[];
  storedOrganizationId?: string | null;
  sessionOrganizationId?: string | null;
}): string[] {
  if (options.userOrganizationIds?.length) {
    return options.userOrganizationIds;
  }
  if (options.storedOrganizationId) {
    return [options.storedOrganizationId];
  }
  if (options.sessionOrganizationId) {
    return [options.sessionOrganizationId];
  }
  return [];
}

export function needsLegalAcceptance(
  userData: User | null,
  legal: GlobalLegalDocument,
  isGodMode: boolean
): boolean {
  if (isGodMode && !userData) {
    return false;
  }
  const userLegal = userData?.legal;
  return (
    userLegal?.acceptTerms !== true ||
    userLegal?.acceptPrivacyPolicy !== true ||
    userLegal?.termsVersion !== legal.terms.version ||
    userLegal?.privacyPolicyVersion !== legal.privacyPolicy.version
  );
}

export async function syncLegalGate(
  userData: User | null,
  isGodMode: boolean,
  setGlobalLegal: (legal: GlobalLegalState) => void,
  setLegalGateOpen: (open: boolean) => void
): Promise<void> {
  try {
    if (isGodMode && !userData) {
      setLegalGateOpen(false);
      return;
    }
    const legal = await getGlobalLegal();
    if (legal?.terms?.version && legal?.privacyPolicy?.version) {
      setGlobalLegal(legal);
      setLegalGateOpen(needsLegalAcceptance(userData, legal, isGodMode));
    } else {
      setLegalGateOpen(false);
    }
  } catch {
    setLegalGateOpen(false);
  }
}
